import React, { Fragment } from 'react';
import StateOptions from 'components/common/state_options';
import _ from 'lodash';
import moment from 'moment';
import { ADDRESSES } from 'components/constants';
import { roundDateTime } from 'components/common/toolbox';

const isDev = (process.env.REACT_APP_FIREBASE_ENV == 'development');

export function service_request_fields(default_duration, addressId, technicianId) {

	return (
		{
			customerId: [],
			addressId,
			addressTypeId: ADDRESSES.SERVICE.id,
			description: '',
			imageUrl: '',
			requestDate: new Date(),
			suggested: '',
			technicianId: technicianId,
			vendorId: [],
			startDate: roundDateTime(moment(), moment.duration(15, "minutes")).toDate(),
			startTime: roundDateTime(moment(), moment.duration(15, "minutes")).toDate(),
			endDate: roundDateTime(moment(), moment.duration(15, "minutes")).add(default_duration, 'hours').toDate(),
			duration: (parseFloat(default_duration ?? '2') * 60).toString(),
		}
	);
};

export function service_request_form_layout({
	firebase, 
	fromSchedule,
	handle,
	newAddress, 
	profile, 
	service_request,
	setNewAddress,
	technicians,
	typeButtons, 
}) {

	const address_type_options = Object.values(ADDRESSES).map((type, index) => <option key={index} value={type.id}>{type.name}</option>);
	const technician_options = _.orderBy(technicians, ['contact.lastName'], ['asc']).map((tech, index) => <option key={index} value={tech.id}>{tech.contact?.firstName + ' ' + tech.contact?.lastName}</option>);

	if (profile?.addresses) {
		var address_options = profile.addresses.map((address, index) => {
			let addressType = _.find(Object.values(ADDRESSES), { id: address.addressTypeId });
			return (
				<option key={'address' + address.id} value={address.id}>
					{`${(address.addressName) ? address.addressName + ' ' : ''}${(addressType) ? '(' + addressType?.name + ') - ' : ''}${address.addressLine1}${(address.addressLine2) ? address.addressLine2 : ''}`}
				</option>
			);
		});
	}

	return (
		[{
			column_class: 'col-md-7',
			body: [{
				section:
					<Fragment>
						<h3 className="m-t-none m-b">Customer Information</h3>
					</Fragment>,
				layout: [{
					grid: 'col-md-12',
					label: 'Customer',
					field: 'customerId',
					placeholder: 'Search for Customer',
					type: 'typeahead',
					valid: ['required'],
					disabled: (!fromSchedule),
					options: {
						allow_new: true,
						allow_new_button: (fromSchedule),
						fields: ["displayName", "addresses", "contacts"],
						id: 'id',
						minLength: 0,
						sort: [{ "displayName": "asc" }],
						table: 'mt-' + handle + (isDev ? '-dev' : '') + '-profiles',
						target: 'displayName',
						type_filter: 'customer',
					},
				},
				...(!newAddress) ? [{
					grid: 'col-12',
					label: 'Service Address',
					field: 'addressId',
					type: 'select',
					valid: ['required'],
					disabled: false,
					options:
						<Fragment>
							<option key={'none'} value={null}>- Select -</option>
							{address_options}
						</Fragment>,
				}] : []
			]}, 
			...(!newAddress) ? [{
				section:
					<Fragment>
						{ !newAddress &&
							<h4 className="d-flex align-items-center">
								<i className="fa-solid fa-circle-plus text-muted fa-2x mr-3" role="button" onClick={ () => setNewAddress(true) }></i> Service Address
							</h4>
						}
					</Fragment>,
				layout: [],
			}] : [],
			...(newAddress) ? [{
				section:
					<Fragment>
						<h3 className="m-t-none m-b">Address 
							<i className="fa-solid fa-circle-minus fa-lg mt-2 text-danger float-right" role="button" onClick={ () => setNewAddress(false) }></i>
						</h3>
					</Fragment>,
				layout: [{
					grid: 'col-4',
					label: 'Address Type',
					field: 'addressTypeId',
					type: 'select',
					valid: ['required'],
					options:
						<Fragment>
							{address_type_options}
						</Fragment>,
				}, {
					grid: 'col-8',
					label: 'Address Name',
					field: 'addressName',
					type: 'text',
				}, {
					grid: 'col-8',
					label: 'Address',
					field: 'addressLine1',
					type: 'location-autocomplete',
					prepend: <i className="fas fa-map-marker-alt"></i>,
					valid: ['required'],
				}, {
					grid: 'col-4',
					label: 'Secondary Address',
					field: 'addressLine2',
					type: 'text',
				}, {
					grid: 'col-4',
					label: 'City',
					field: 'city',
					type: 'text',
				}, {
					grid: 'col-4',
					label: 'State',
					field: 'state',
					type: 'select',
					options:
						<Fragment>
							<option value="">- Select -</option>
							<StateOptions />,
						</Fragment>,
				}, {
					grid: 'col-4',
					label: 'Zip',
					field: 'zip',
					type: 'text',
				}],
			}] : [], 
			{
				section:
					<Fragment>
						<h3 className="m-t-none m-b">{`Service Request Details`}</h3>
						{ typeButtons }
					</Fragment>,
				layout: [{
					grid: 'col-sm-4',
					label: `Request Date`,
					field: 'requestDate',
					type: 'date',
					dateFormat: 'MM-DD-YYYY',
					showTimeSelect: true,
					valid: ['required'],
					selectsStart: true,
					// readOnly: (isComplete) ? true : false,
				}, {
					grid: 'col-sm-8',
					label: 'Technician',
					field: 'technicianId',
					type: (service_request.id) ? 'skip' : 'select',
					options:
						<Fragment>
							<option key={'none'} value={''}>- UNASSIGNED -</option>
							{technician_options}
						</Fragment>,
				}, {
					grid: 'col-4',
					label: 'Start Date',
					field: 'startDate',
					type: 'date',
					dateFormat: 'MM-DD-YYYY',
					valid: ['required'],
				}, {
					grid: 'col-4',
					label: 'Start Time',
					field: 'startTime',
					type: 'time',
					dateFormat: 'hh:mm A',
					valid: ['required'],
				}, {
					grid: 'col-4',
					label: 'Duration',
					field: 'duration',
					valid: ['required'],
					type: 'select',
					options:
						<Fragment>
							<option key={'30'} value={'30'}>00:30 hrs</option>
							<option key={'60'} value={'60'}>01:00 hrs</option>
							<option key={'90'} value={'90'}>01:30 hrs</option>
							<option key={'120'} value={'120'}>02:00 hrs</option>
							<option key={'150'} value={'150'}>02:30 hrs</option>
							<option key={'180'} value={'180'}>03:00 hrs</option>
							<option key={'210'} value={'210'}>03:30 hrs</option>
							<option key={'240'} value={'240'}>04:00 hrs</option>
							<option key={'270'} value={'270'}>04:30 hrs</option>
							<option key={'300'} value={'300'}>05:00 hrs</option>
							<option key={'330'} value={'330'}>05:30 hrs</option>
							<option key={'360'} value={'360'}>06:00 hrs</option>
							<option key={'390'} value={'390'}>06:30 hrs</option>
							<option key={'420'} value={'420'}>07:00 hrs</option>
							<option key={'450'} value={'450'}>07:30 hrs</option>
							<option key={'480'} value={'480'}>08:00 hrs</option>
						</Fragment>,
				}, {
					grid: 'col-12',
					label: 'Description of Issue',
					field: 'description',
					type: 'textarea',
					valid: ['required'],
				}],
			}]
		}, {
			column_class: 'col-md-5',
			body: [{
				section:
					<Fragment>
						<h3 className="m-t-none m-b">Main Service Request Image</h3>
					</Fragment>,
				layout: [{
					grid: 'col-12',
					label: 'Service Request Image',
					field: 'imageUrl',
					type: 'dropzone',
					dropzone: {
						camera: true,
						storage: 'firebase',
						storageRef: firebase.storage().ref(),
						bin: handle,
						directory: 'service-requests',
						className: 'mb-3',
						height: "280px",
						multiple: false,
					}
				}],
			}],
		}]
	);
};